import {
  ListByIdDocument,
  OperatorWithStatus,
  useListByIdQuery,
  useListUpdateByIdMutation,
  useOperatorsByIdsLazyQuery,
} from "gql/generated";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { getMultilingualString } from "shared/utils";
import { mapOperatorCSVData } from "shared/utils/mapOperatorCSVData";
import { getOperatorCSVHeaders } from "shared/utils";
import {
  CustomListDetailsPageActionKey,
  customListDetailsViewSections,
} from "./_customListDetails.config";

export const useCustomListDetails = () => {
  const { t } = useTranslation("core");
  const { toastFactory } = useToastContext();

  // Load the list details
  const { listId } = useParams();
  const { data, error, loading } = useListByIdQuery({
    variables: { id: listId },
  });
  const list = data?.listById;

  // The "Add to favorites" action becomes "Remove from favorites" is the list is favorited
  // So, we watch for when changes to the list to determine how to display that PageAction
  const [pageActionToDisable, setPageActionToDisable] = useState("");
  const [pageActions, setPageActions] = useState(customListDetailsViewSections);
  useEffect(() => {
    setPageActions((prev) =>
      prev.map((item) =>
        item.key == CustomListDetailsPageActionKey.AddToFavorites
          ? {
              ...item,
              useAlternateInfo: data?.listById.favorited,
            }
          : item.key === CustomListDetailsPageActionKey.ArchiveList
            ? {
                ...item,
                useAlternateInfo: data?.listById.archived,
              }
            : item,
      ),
    );
  }, [data?.listById]);

  // Handle showing/hiding of the list editing modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const handleEditListItemsClick = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };

  const handleActionClick = (key: string) => {
    setPageActionToDisable(key);
    switch (key) {
      case CustomListDetailsPageActionKey.AddToFavorites:
        favoriteList();
        break;
      case CustomListDetailsPageActionKey.ExportToCsv:
        exportToCSV();
        break;
      case CustomListDetailsPageActionKey.ArchiveList:
        archiveList();
        break;
    }
  };

  // Handle adding to / removing from favorites
  const [listUpdateByIdMutation, { loading: loadingFavouriteList }] =
    useListUpdateByIdMutation();
  const favoriteList = () => {
    if (!list) {
      return;
    }
    const favorited = !list?.favorited;
    listUpdateByIdMutation({
      variables: {
        id: list._id,
        record: {
          favorited,
        },
      },
      refetchQueries: [ListByIdDocument],
    })
      .then((response) => {
        if (!response.errors) {
          toastFactory.create({
            title: t("SUCCESS"),
            description: t("FAVORITES.ACTION.SUCCESS", {
              addedOrRemoved: favorited
                ? t("FAVORITES.ACTION.ADDED_TO")
                : t("FAVORITES.ACTION.REMOVED_FROM"),
              listName: getMultilingualString(list.name ?? {}),
            }),
          });
        } else {
          toastFactory.create({
            title: t("ERROR.TITLE"),
            description: `${t("FAVORITES.ACTION.ERROR", { listName: getMultilingualString(list.name) })}. ${t("FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
          });
        }
      })
      .catch(() => {
        toastFactory.create({
          title: t("ERROR.TITLE"),
          description: `${t("FAVORITES.ACTION.ERROR", { listName: getMultilingualString(list.name) })}. ${t("FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
        });
      })
      .finally(() => setPageActionToDisable(""));
  };

  /**
   * Achive/Unarchive a list
   * @returns
   */
  const archiveList = () => {
    if (!list) {
      return;
    }
    const archived = !list.archived;
    listUpdateByIdMutation({
      variables: {
        id: list._id,
        record: {
          archived,
        },
      },
      refetchQueries: [ListByIdDocument],
    })
      .then((response) => {
        if (!response.errors) {
          toastFactory.create({
            title: t("SUCCESS"),
            description: t("ARCHIVE.ACTION.SUCCESS", {
              archivedOrUnarchived: archived
                ? t("ARCHIVE.ACTION.ARCHIVED")
                : t("ARCHIVE.ACTION.UNARCHIVED"),
              entityName: getMultilingualString(list.name ?? {}),
            }),
          });
        } else {
          toastFactory.create({
            title: t("ERROR.TITLE"),
            description: `${t("ARCHIVE.ACTION.ERROR", { entityName: getMultilingualString(list.name) })}. ${t("FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
          });
        }
      })
      .catch(() => {
        toastFactory.create({
          title: t("ERROR.TITLE"),
          description: `${t("ARCHIVE.ACTION.ERROR", { entityName: getMultilingualString(list.name) })}. ${t("FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
        });
      })
      .finally(() => setPageActionToDisable(""));
  };

  /**
   * Export the details for the current list of operators to CSV
   */
  const [getOperatorsByIds, { loading: isLoadingCSV }] =
    useOperatorsByIdsLazyQuery();
  const [filteredOperatorEntityIds, setFilteredOperatorEntityIds] = useState<
    string[]
  >([]);
  const exportToCSV = async () => {
    try {
      const operatorIds = await getOperatorsByIds({
        variables: {
          operatorEntityIds: filteredOperatorEntityIds,
        },
      });

      const csvData = mapOperatorCSVData(t, operatorIds.data);
      const listName = getMultilingualString(list?.name ?? {});
      const filename = `${listName || "wb-list-operators"}`;

      const csvConfig = mkConfig({
        columnHeaders: getOperatorCSVHeaders(t),
        filename,
      });
      const csv = generateCsv(csvConfig)(csvData);
      download(csvConfig)(csv);
    } catch {
      toastFactory.create({
        title: t("ERROR.TITLE"),
        description: t("CSV.ERROR"),
      });
    }
    setPageActionToDisable("");
  };

  const [operatorModalOperatorId, setOperatorModalOperatorId] = useState("");
  const handleOperatorDetailsClick = (operator: OperatorWithStatus) =>
    setOperatorModalOperatorId(operator._entityId);

  return {
    loading,
    error,
    list,
    isEditModalOpen,
    isLoadingCSV,
    loadingFavouriteList,
    operatorModalOperatorId,
    pageActions,
    pageActionToDisable,
    handleActionClick,
    handleEditListItemsClick,
    handleOperatorDetailsClick,
    setFilteredOperatorEntityIds,
    setOperatorModalOperatorId,
  };
};
